import React, { useEffect, useState } from "react";

import ErrorBoundaryWithPropagation from "@components/boundary/ErrorBoundaryWithPropagation";
import { LiveWidgetTypes } from "@data-types/live-widget/LiveWidgetTypes";
import { useLiveWidgetStore } from "@state/hooks/live-widgets";
import useLmtLsEventStore from "@state/hooks/side-widgets/useLiveEventMenuViewStore";
import { THEME_SETTINGS } from "@theme-settings";
import { observer } from "mobx-react";

import { TSideWidgetView } from "./TSideWidgetView";
import {
	SideWidgetBody,
	SideWidgetDraggableContainer,
	SideWidgetHeader,
} from "./components";

export const SideLiveWidget = observer(() => {
	const [headerRef, setHeaderRef] = useState<HTMLDivElement | null>(null);
	const isFirstMount = React.useRef(true);
	const lmtLsStore = useLmtLsEventStore();
	const store = useLiveWidgetStore();
	const activeSideWidget = store.activeSideWidget;
	const [currentView, setCurrentView] = useState<TSideWidgetView>(
		activeSideWidget?.type === LiveWidgetTypes.LMT
			? TSideWidgetView.LIVE_MATCH_TRACKER
			: TSideWidgetView.LIVE_STREAM
	);

	if (
		!THEME_SETTINGS.features.media.isLiveMatchTrackerEnabled ||
		!THEME_SETTINGS.features.media.isLiveStreamEnabled
	) {
		return null;
	}

	useEffect(() => {
		if (!isFirstMount.current) {
			if (activeSideWidget != null) {
				store.expandSideWidget();
			} else if (currentView != TSideWidgetView.EVENT_LIST) {
				store.collapseSideWidget();
			}
		}
		isFirstMount.current = false;
	}, [activeSideWidget?.eventId, currentView]);

	useEffect(() => {
		if (!activeSideWidget || !lmtLsStore.initialized) {
			return;
		}

		const event = lmtLsStore.eventsMap.get(activeSideWidget.eventId);
		if (!event || event.source?.matchId == null) {
			store.handleExeFeedSourceRemoved(activeSideWidget.eventId);
		}
	}, [activeSideWidget?.eventId, lmtLsStore.lmtEvents]);

	return (
		<ErrorBoundaryWithPropagation componentName={"Desktop/SideWidget.tsx"}>
			<SideWidgetDraggableContainer headerRef={headerRef}>
				<SideWidgetHeader
					headerRef={headerRef}
					setHeaderRef={setHeaderRef}
					currentView={currentView}
					setView={setCurrentView}
					lmtLsStore={lmtLsStore}
				/>
				<SideWidgetBody
					view={currentView}
					lmtLsEventStore={lmtLsStore}
					setView={setCurrentView}
				/>
			</SideWidgetDraggableContainer>
		</ErrorBoundaryWithPropagation>
	);
});
